import fetchClient from './fetchClient';
import { errorHandler } from './utils';

/**
 * BI dashboard API client.
 *
 * Every call targets the pre-aggregated `/bi/*` endpoints (never the raw
 * transactional data) and returns the parsed `{ data, meta, summary }` payload,
 * or `null` on failure (errors are surfaced through the shared errorHandler).
 */

const config = () => ({
    headers: {
        Authorization: 'Bearer ' + localStorage.getItem('token'),
    },
});

export type Granularity = 'day' | 'week' | 'month' | 'year';

export interface BiRange {
    from?: string; // YYYY-MM-DD
    to?: string; // YYYY-MM-DD
}

/** Appends only the defined query params. */
function qs(params: any): string {
    const sp = new URLSearchParams();
    Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
    });
    const s = sp.toString();
    return s ? `?${s}` : '';
}

/* ------------------------------- KPI #1 — Daily revenue ------------------------------- */
export const getBiDailyRevenue = async (
    params: BiRange & { page?: number; limit?: number },
) => {
    const res: any = await fetchClient
        .get(`/bi/revenue/daily${qs(params)}`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/* ------------------------------- KPI #2 — Product margin ------------------------------ */
export const getBiProductMargin = async (
    params: BiRange & {
        page?: number;
        limit?: number;
        sort?: 'margin' | 'revenue' | 'quantity';
        onlyWithPurchase?: boolean;
    },
) => {
    const res: any = await fetchClient
        .get(`/bi/margin/products${qs(params)}`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/* ------------------------------- KPI #3 — Client margin ------------------------------- */
export const getBiClientMargin = async (
    params: BiRange & {
        granularity?: 'month' | 'year';
        page?: number;
        limit?: number;
        groupByClient?: boolean;
    },
) => {
    const res: any = await fetchClient
        .get(`/bi/margin/clients${qs(params)}`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/* ------------------------------- KPI #4 — Unpaid invoices ----------------------------- */
export const getBiUnpaidInvoices = async (
    params: { status?: 'late' | 'pending'; page?: number; limit?: number },
) => {
    const res: any = await fetchClient
        .get(`/bi/invoices/unpaid${qs(params)}`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/* ----------------------------- KPI #5 & #6 — Prospect funnel -------------------------- */
export const getBiProspects = async (
    params: BiRange & { granularity?: Granularity; page?: number; limit?: number },
) => {
    const res: any = await fetchClient
        .get(`/bi/prospects${qs(params)}`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/* ------------------------------- KPI #7 — Pareto 20/80 -------------------------------- */
export const getBiPareto = async (params: { page?: number; limit?: number }) => {
    const res: any = await fetchClient
        .get(`/bi/products/pareto${qs(params)}`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/* ------------------------------- KPI #8 — Top products -------------------------------- */
export const getBiTopProducts = async (
    params: BiRange & { metric?: 'revenue' | 'qty'; limit?: number },
) => {
    const res: any = await fetchClient
        .get(`/bi/products/top${qs(params)}`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/** Triggers a manual aggregate rebuild (owner only). Returns per-job results. */
export const triggerBiRebuild = async () => {
    const res: any = await fetchClient
        .post(`/bi/admin/rebuild`, {}, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};

/** Returns BI collection status (document counts + watermarks). */
export const getBiStatus = async () => {
    const res: any = await fetchClient
        .get(`/bi/admin/status`, config())
        .catch(errorHandler);
    if (!res || res.error) return null;
    return res.data;
};
